import React from 'react';

/**
 * Capacitor Platform Helper
 * Works with or without Capacitor installed
 * Detects platform (iOS, Android, Web) and provides utilities
 */

// Check if Capacitor is available
let Capacitor, Device, StatusBar, Style, SafeArea;

try {
  const CapacitorCore = require('@capacitor/core');
  Capacitor = CapacitorCore.Capacitor;
} catch (e) {
  console.log('Capacitor not installed - running in web mode');
}

try {
  const DeviceModule = require('@capacitor/device');
  Device = DeviceModule.Device;
} catch (e) {
  // Device plugin not available
}

try {
  const StatusBarModule = require('@capacitor/status-bar');
  StatusBar = StatusBarModule.StatusBar;
  Style = StatusBarModule.Style;
} catch (e) {
  // StatusBar plugin not available
}

try {
  const SafeAreaModule = require('capacitor-plugin-safe-area');
  SafeArea = SafeAreaModule.SafeArea;
} catch (e) {
  // SafeArea plugin not available
}

// Platform detection (works without Capacitor)
export const isNative = () => {
  if (Capacitor) {
    return Capacitor.isNativePlatform();
  }
  // Fallback detection
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

export const isIOS = () => {
  if (Capacitor) {
    return Capacitor.getPlatform() === 'ios';
  }
  // Fallback detection
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const isAndroid = () => {
  if (Capacitor) {
    return Capacitor.getPlatform() === 'android';
  }
  // Fallback detection
  return /Android/i.test(navigator.userAgent);
};

export const isWeb = () => {
  if (Capacitor) {
    return Capacitor.getPlatform() === 'web';
  }
  // Fallback - if not iOS or Android, it's web
  return !isIOS() && !isAndroid();
};

export const getPlatform = () => {
  if (Capacitor) {
    return Capacitor.getPlatform();
  }
  // Fallback detection
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
};

/**
 * Get device information
 */
export const getDeviceInfo = async () => {
  if (!Device) {
    // Fallback device info
    return {
      platform: getPlatform(),
      operatingSystem: getPlatform(),
      osVersion: 'unknown',
      manufacturer: 'unknown',
      model: navigator.userAgent,
      isVirtual: false,
      webViewVersion: 'unknown'
    };
  }

  try {
    const info = await Device.getInfo();
    return {
      platform: info.platform,
      operatingSystem: info.operatingSystem,
      osVersion: info.osVersion,
      manufacturer: info.manufacturer,
      model: info.model,
      isVirtual: info.isVirtual,
      webViewVersion: info.webViewVersion
    };
  } catch (error) {
    console.error('Error getting device info:', error);
    return null;
  }
};

/**
 * Configure status bar for mobile
 */
export const configureStatusBar = async (options = {}) => {
  if (!isNative() || !StatusBar) return;

  try {
    const {
      style = Style?.Light || 'Light',
      backgroundColor = '#2c3e50',
      overlay = false
    } = options;

    if (isAndroid() && StatusBar.setBackgroundColor) {
      await StatusBar.setBackgroundColor({ color: backgroundColor });
    }
    
    if (StatusBar.setStyle) {
      await StatusBar.setStyle({ style });
    }
    
    if (StatusBar.setOverlaysWebView) {
      await StatusBar.setOverlaysWebView({ overlay });
    }
  } catch (error) {
    console.error('Status bar configuration error:', error);
  }
};

/**
 * Get safe area insets for notch/status bar handling
 */
export const getSafeAreaInsets = async () => {
  if (!isNative()) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  if (SafeArea) {
    try {
      const safeArea = await SafeArea.getSafeAreaInsets();
      return safeArea.insets;
    } catch (error) {
      console.error('Safe area error:', error);
    }
  }

  // Fallback values
  return {
    top: isIOS() ? 44 : 24,
    right: 0,
    bottom: isIOS() ? 34 : 0,
    left: 0
  };
};

/**
 * Apply platform-specific padding
 */
export const getPlatformPadding = (safeAreaInsets) => {
  return {
    paddingTop: safeAreaInsets.top,
    paddingRight: safeAreaInsets.right,
    paddingBottom: safeAreaInsets.bottom,
    paddingLeft: safeAreaInsets.left
  };
};

/**
 * Get platform-specific styles
 */
export const getPlatformStyles = () => {
  const platform = getPlatform();
  
  const baseStyles = {
    '--safe-area-top': '0px',
    '--safe-area-bottom': '0px',
    '--safe-area-left': '0px',
    '--safe-area-right': '0px'
  };

  if (isIOS()) {
    return {
      ...baseStyles,
      '--safe-area-top': 'env(safe-area-inset-top)',
      '--safe-area-bottom': 'env(safe-area-inset-bottom)',
      '--safe-area-left': 'env(safe-area-inset-left)',
      '--safe-area-right': 'env(safe-area-inset-right)',
      '--status-bar-height': '44px',
      '--bottom-safe-area': '34px'
    };
  }

  if (isAndroid()) {
    return {
      ...baseStyles,
      '--status-bar-height': '24px',
      '--bottom-safe-area': '0px'
    };
  }

  return baseStyles;
};

/**
 * Custom React Hook for Capacitor platform detection
 */
export const useCapacitor = () => {
  const [platform, setPlatform] = React.useState({
    isNative: isNative(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isWeb: isWeb(),
    platform: getPlatform(),
    deviceInfo: null,
    safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 }
  });

  React.useEffect(() => {
    const loadPlatformData = async () => {
      const deviceInfo = await getDeviceInfo();
      const safeAreaInsets = await getSafeAreaInsets();
      
      setPlatform({
        isNative: isNative(),
        isIOS: isIOS(),
        isAndroid: isAndroid(),
        isWeb: isWeb(),
        platform: getPlatform(),
        deviceInfo,
        safeAreaInsets
      });
    };

    loadPlatformData();
  }, []);

  return platform;
};

/**
 * Platform-specific component wrappers
 */
export const IOSOnly = ({ children }) => {
  return isIOS() ? children : null;
};

export const AndroidOnly = ({ children }) => {
  return isAndroid() ? children : null;
};

export const WebOnly = ({ children }) => {
  return isWeb() ? children : null;
};

export const NativeOnly = ({ children }) => {
  return isNative() ? children : null;
};

/**
 * Get platform-specific CSS class
 */
export const getPlatformClass = () => {
  const classes = ['platform'];
  
  if (isNative()) classes.push('platform-native');
  if (isIOS()) classes.push('platform-ios');
  if (isAndroid()) classes.push('platform-android');
  if (isWeb()) classes.push('platform-web');
  
  return classes.join(' ');
};

/**
 * Initialize Capacitor app
 */
export const initializeCapacitor = async (options = {}) => {
  const {
    statusBarStyle = Style?.Light || 'Light',
    statusBarColor = '#2c3e50',
    enableSafeArea = true
  } = options;

  try {
    // Configure status bar (only if available)
    if (StatusBar) {
      await configureStatusBar({
        style: statusBarStyle,
        backgroundColor: statusBarColor
      });
    }

    // Apply platform class to body
    document.body.classList.add(...getPlatformClass().split(' '));

    // Get safe area insets
    const safeAreaInsets = await getSafeAreaInsets();

    // Apply safe area CSS variables
    if (enableSafeArea) {
      const root = document.documentElement;
      root.style.setProperty('--safe-area-top', `${safeAreaInsets.top}px`);
      root.style.setProperty('--safe-area-bottom', `${safeAreaInsets.bottom}px`);
      root.style.setProperty('--safe-area-left', `${safeAreaInsets.left}px`);
      root.style.setProperty('--safe-area-right', `${safeAreaInsets.right}px`);
    }

    console.log('Platform initialized successfully', {
      platform: getPlatform(),
      capacitorInstalled: !!Capacitor,
      safeAreaInsets
    });

    return {
      platform: getPlatform(),
      safeAreaInsets,
      capacitorAvailable: !!Capacitor
    };
  } catch (error) {
    console.error('Platform initialization error:', error);
    return null;
  }
};

export default {
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  getPlatform,
  getDeviceInfo,
  configureStatusBar,
  getSafeAreaInsets,
  getPlatformPadding,
  getPlatformStyles,
  useCapacitor,
  IOSOnly,
  AndroidOnly,
  WebOnly,
  NativeOnly,
  getPlatformClass,
  initializeCapacitor
};

